import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { Calendar, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { CT, todayYmd } from '../../../lib/dateRange';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { monthCells } from './DateRangeCalendar';

// One date, picked in the app's own calendar (Nic 2026-10-26: the browser's
// date box looked out of place on the Intent to File step). One click picks
// and closes; the month and year menus jump back years at once (an Intent to
// File can be old); Today and Clear underneath. Days after `max` can't be
// picked. The value is 'YYYY-MM-DD' or null, the same as the date box it
// replaces.

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = moment.months();
const MONTHS_SHORT = moment.monthsShort();
const FIRST_YEAR = 1990;

// The header jumps (Nic 2026-10-26): the browser's own month and year menus
// looked dated and could run off the page, so click "October 2026" for a
// grid of years, then months, then the day, all inside the calendar.
const HEADER =
  'inline-flex h-8 items-center gap-1 rounded-lg px-2 text-[13px] font-semibold text-slate-800 outline-none hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:text-slate-100 dark:hover:!bg-white/10';
const GRID_CELL =
  'flex items-center justify-center rounded-lg text-[12.5px] font-medium tabular-nums outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 disabled:cursor-not-allowed disabled:opacity-30';
const GRID_ON = 'bg-emerald-600 font-semibold text-white';
const GRID_OFF =
  'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:!bg-white/10';
const ARROW =
  'flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 outline-none hover:bg-slate-100 hover:text-slate-700 focus:outline-none disabled:opacity-30 dark:hover:!bg-white/10 dark:hover:text-slate-200';

export const DateCalendar = ({ value, onPick, onClear, max = todayYmd() }) => {
  const anchorOf = () => moment.tz(value || max, 'YYYY-MM-DD', CT);
  const [view, setView] = useState(() => ({
    y: anchorOf().year(),
    m: anchorOf().month(),
  }));
  const [mode, setMode] = useState('days');
  const yearsRef = useRef(null);
  // The years grid opens scrolled to the year in view.
  useEffect(() => {
    if (mode !== 'years') return;
    yearsRef.current
      ?.querySelector('[data-current="true"]')
      ?.scrollIntoView?.({ block: 'center' });
  }, [mode]);
  useEffect(() => {
    if (value) setView({ y: anchorOf().year(), m: anchorOf().month() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const cells = useMemo(() => monthCells(view.y, view.m), [view]);
  const today = todayYmd();
  const maxM = moment.tz(max, 'YYYY-MM-DD', CT);
  const years = [];
  for (let y = maxM.year(); y >= FIRST_YEAR; y -= 1) years.push(y);
  const step = (dir) => {
    const next = moment
      .tz(CT)
      .year(view.y)
      .month(view.m)
      .date(1)
      .add(dir, 'month');
    setView({ y: next.year(), m: next.month() });
  };
  const atLast =
    view.y > maxM.year() || (view.y === maxM.year() && view.m >= maxM.month());

  return (
    <div className="w-[264px]">
      <div className="mb-2 flex items-center justify-between gap-1">
        <button
          type="button"
          onClick={() => setMode(mode === 'days' ? 'years' : 'days')}
          aria-label={
            mode === 'days' ? 'Pick a month and year' : 'Back to the days'
          }
          className={HEADER}
        >
          {mode === 'years'
            ? 'Pick a year'
            : mode === 'months'
              ? view.y
              : `${MONTHS[view.m]} ${view.y}`}
          <ChevronDown
            size={14}
            className={cn(
              'opacity-60 transition-transform',
              mode !== 'days' && 'rotate-180',
            )}
          />
        </button>
        {mode === 'days' ? (
          <span className="flex gap-0.5">
            <button
              type="button"
              onClick={() => step(-1)}
              aria-label="Previous month"
              className={ARROW}
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              disabled={atLast}
              aria-label="Next month"
              className={ARROW}
            >
              <ChevronRight size={16} />
            </button>
          </span>
        ) : null}
      </div>
      {mode === 'years' ? (
        <div
          ref={yearsRef}
          className="grid max-h-[232px] grid-cols-4 gap-1 overflow-y-auto [scrollbar-width:thin]"
        >
          {years.map((yr) => (
            <button
              key={yr}
              type="button"
              data-current={yr === view.y}
              onClick={() => {
                const m =
                  yr === maxM.year() ? Math.min(view.m, maxM.month()) : view.m;
                setView({ y: yr, m });
                setMode('months');
              }}
              className={cn(
                GRID_CELL,
                'h-8',
                yr === view.y ? GRID_ON : GRID_OFF,
              )}
            >
              {yr}
            </button>
          ))}
        </div>
      ) : mode === 'months' ? (
        <div className="grid grid-cols-3 gap-1">
          {MONTHS_SHORT.map((label, i) => (
            <button
              key={label}
              type="button"
              disabled={view.y === maxM.year() && i > maxM.month()}
              onClick={() => {
                setView({ ...view, m: i });
                setMode('days');
              }}
              className={cn(
                GRID_CELL,
                'h-10',
                i === view.m ? GRID_ON : GRID_OFF,
              )}
            >
              {label}
            </button>
          ))}
        </div>
      ) : (
        <>
          <div className="mb-1 grid grid-cols-7">
            {WEEKDAYS.map((d, i) => (
              <div
                key={`${d}-${i}`}
                className="text-center text-[10px] font-semibold uppercase tracking-wide text-slate-400"
              >
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((date, i) => {
              if (!date) return <div key={`blank-${i}`} className="h-8" />;
              const future = date > max;
              const on = date === value;
              return (
                <button
                  key={date}
                  type="button"
                  disabled={future}
                  onClick={() => onPick(date)}
                  aria-label={moment
                    .tz(date, 'YYYY-MM-DD', CT)
                    .format('MMMM D, YYYY')}
                  aria-pressed={on}
                  className={cn(
                    'relative flex h-8 w-full items-center justify-center rounded-full text-[12px] font-medium outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60',
                    future &&
                      'cursor-not-allowed text-slate-300 dark:text-slate-600',
                    !future &&
                      !on &&
                      'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:!bg-white/10',
                    on && 'bg-emerald-600 text-white',
                  )}
                >
                  {Number(date.slice(8, 10))}
                  {date === today && !on ? (
                    <span className="absolute inset-x-0 bottom-1 mx-auto h-1 w-1 rounded-full bg-emerald-500" />
                  ) : null}
                </button>
              );
            })}
          </div>
        </>
      )}
      <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-2 dark:border-white/10">
        <button
          type="button"
          onClick={onClear}
          disabled={!value}
          className="rounded-md px-2 py-1 text-xs font-semibold text-slate-500 outline-none hover:bg-slate-100 focus:outline-none disabled:opacity-40 dark:text-slate-400 dark:hover:!bg-white/10"
        >
          Clear
        </button>
        <button
          type="button"
          onClick={() => onPick(today <= max ? today : max)}
          className="rounded-md px-2 py-1 text-xs font-semibold text-emerald-700 outline-none hover:bg-emerald-50 focus:outline-none dark:text-emerald-300 dark:hover:!bg-emerald-500/10"
        >
          Today
        </button>
      </div>
    </div>
  );
};

// The field: a button showing the date (green once set, like the step's
// other answers), opening the calendar beside it.
const DatePickerField = ({
  value,
  onChange,
  disabled = false,
  max,
  placeholder = 'Pick a date',
  label = 'Pick a date',
}) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={(v) => !disabled && setOpen(v)}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={
            value
              ? `${label}: ${moment.tz(value, 'YYYY-MM-DD', CT).format('MMMM D, YYYY')}`
              : label
          }
          className={cn(
            'inline-flex h-9 items-center gap-2 rounded-lg border-2 px-3 text-[13px] font-medium outline-none transition-[border-color,box-shadow] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 disabled:opacity-60',
            value
              ? 'border-emerald-500 bg-emerald-500/5 text-emerald-700 dark:text-emerald-300'
              : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 dark:border-white/10 dark:!bg-white/5 dark:text-slate-300',
          )}
        >
          <Calendar size={14} className="shrink-0 opacity-70" />
          {value
            ? moment.tz(value, 'YYYY-MM-DD', CT).format('MMM D, YYYY')
            : placeholder}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" aria-label={label}>
        <DateCalendar
          value={value || null}
          max={max}
          onPick={(d) => {
            setOpen(false);
            onChange(d);
          }}
          onClear={() => {
            setOpen(false);
            onChange(null);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

export default DatePickerField;
